import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, ExternalLink, Archive as ArchiveIcon, CheckCircle2, AlertTriangle } from "lucide-react";

const CLOSED = ["Not Used", "Rejected", "Cancelled", "Internal Use"];

function ArchiveStatusBadge({ status }) {
  const archived = status === "Completed";
  if (archived) {
    return <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300 px-2.5 py-0.5 text-[11px] font-semibold"><CheckCircle2 className="h-3 w-3" /> ARCHIVED</span>;
  }
  if (status === "Source Link Missing") {
    return <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300 px-2.5 py-0.5 text-[11px] font-semibold"><AlertTriangle className="h-3 w-3" /> NOT FULLY ARCHIVED</span>;
  }
  return <span className="inline-flex items-center rounded-full bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300 px-2.5 py-0.5 text-[11px] font-semibold">{status || "—"}</span>;
}

export default function Archive() {
  const [config, setConfig] = useState({ work_categories: [], archive_statuses: [] });
  const [users, setUsers] = useState([]);
  const [assets, setAssets] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [owner, setOwner] = useState("all");
  const [status, setStatus] = useState("all");

  useEffect(() => {
    api.get("/config").then((r) => setConfig(r.data));
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/assets").then((r) => setAssets(r.data || []));
  }, []);

  const completed = assets.filter((a) => a.current_stage === "Complete");
  const rows = completed.filter((a) => {
    if (q) {
      const s = `${a.work_item_id} ${a.work_item || a.title} ${a.work_category || ""} ${a.sub_category || ""} ${a.work_lead_name || ""}`.toLowerCase();
      if (!s.includes(q.toLowerCase())) return false;
    }
    if (cat !== "all" && (a.work_category || a.type) !== cat) return false;
    if (owner !== "all" && (a.work_lead_id || a.owner_id) !== owner) return false;
    if (status !== "all") {
      if (status === "ARCHIVED" && a.archive_status !== "Completed") return false;
      if (status === "NOT_ARCHIVED" && a.archive_status !== "Source Link Missing") return false;
      if (status !== "ARCHIVED" && status !== "NOT_ARCHIVED" && a.archive_status !== status) return false;
    }
    return true;
  }).sort((a, b) => new Date(b.completed_at || 0) - new Date(a.completed_at || 0));

  const stats = {
    total: completed.length,
    archived: completed.filter((a) => a.archive_status === "Completed").length,
    notArchived: completed.filter((a) => a.archive_status === "Source Link Missing").length,
  };

  return (
    <div className="fade-up space-y-6" data-testid="archive-page">
      <p className="text-muted-foreground text-sm max-w-2xl">A permanent record of every completed creative work item. Items are archived automatically when they reach COMPLETE.</p>

      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-xl border border-border bg-card px-4 py-3" data-testid="archive-stat-total"><p className="font-display font-extrabold text-2xl">{stats.total}</p><p className="text-[11px] text-muted-foreground uppercase tracking-wider">Completed</p></div>
        <div className="rounded-xl border border-border bg-card px-4 py-3" data-testid="archive-stat-archived"><p className="font-display font-extrabold text-2xl text-emerald-600">{stats.archived}</p><p className="text-[11px] text-muted-foreground uppercase tracking-wider">Archived</p></div>
        <div className="rounded-xl border border-border bg-card px-4 py-3" data-testid="archive-stat-notarchived"><p className="font-display font-extrabold text-2xl text-amber-600">{stats.notArchived}</p><p className="text-[11px] text-muted-foreground uppercase tracking-wider">Not Fully Archived</p></div>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input data-testid="archive-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search work item, ID, owner…" className="pl-9" />
        </div>
        <Select value={cat} onValueChange={setCat}>
          <SelectTrigger data-testid="archive-category-filter" className="w-52"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All categories</SelectItem>{(config.work_categories || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={owner} onValueChange={setOwner}>
          <SelectTrigger data-testid="archive-owner-filter" className="w-44"><SelectValue placeholder="Owner" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All owners</SelectItem>{users.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="archive-status-filter" className="w-52"><SelectValue placeholder="Archive status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="ARCHIVED">Archived</SelectItem>
            <SelectItem value="NOT_ARCHIVED">Not Fully Archived</SelectItem>
            {CLOSED.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-xl border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm min-w-[1000px]">
          <thead>
            <tr className="border-b border-border bg-muted/40 text-[11px] uppercase tracking-wider text-muted-foreground">
              {["Work Item ID", "Work Item", "Work Category", "Creative Type", "Creative Owner", "Completed Date", "Archive Status", "Google Drive Source", "File Path / Location"].map((h) => <th key={h} className="text-left font-semibold px-3 py-3 whitespace-nowrap">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((a) => (
              <tr key={a.asset_id} data-testid={`archive-row-${a.asset_id}`} className="border-b border-border last:border-0 hover:bg-accent/40">
                <td className="px-3 py-2.5 font-mono text-primary font-semibold whitespace-nowrap">{a.work_item_id}</td>
                <td className="px-3 py-2.5 font-medium max-w-[220px] truncate">{a.work_item || a.title}</td>
                <td className="px-3 py-2.5 whitespace-nowrap">{a.work_category || a.type || "—"}</td>
                <td className="px-3 py-2.5 text-muted-foreground whitespace-nowrap">{a.sub_category || "—"}</td>
                <td className="px-3 py-2.5 whitespace-nowrap">{a.work_lead_name || a.owner_name || "—"}</td>
                <td className="px-3 py-2.5 whitespace-nowrap text-muted-foreground">{a.completed_at ? fmtDate(a.completed_at) : "—"}</td>
                <td className="px-3 py-2.5"><ArchiveStatusBadge status={a.archive_status} /></td>
                <td className="px-3 py-2.5">{a.drive_source_link ? <a href={a.drive_source_link} target="_blank" rel="noreferrer" className="text-primary inline-flex items-center gap-1"><ExternalLink className="h-3.5 w-3.5" /> Open</a> : <span className="text-amber-600 text-xs">Missing</span>}</td>
                <td className="px-3 py-2.5 max-w-[220px]">{a.file_path ? <span className="text-xs font-mono break-all" data-testid={`file-path-${a.asset_id}`}>{a.file_path}</span> : <span className="text-amber-600 text-xs">Missing</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && (
          <div className="py-16 text-center text-muted-foreground">
            <ArchiveIcon className="h-10 w-10 mx-auto opacity-40" />
            <p className="mt-3">No completed work items match your filters.</p>
          </div>
        )}
      </div>
    </div>
  );
}
